<aside
  class="flex flex-col h-full bg-[var(--bg-panel)] text-[var(--text-main)] transition-[width] duration-300 ease-in-out border-r border-[#ffffff10] shadow-[4px_0_24px_rgba(0,0,0,0.1)] relative"
  [class.w-64]="!isCollapsed()" [class.w-20]="isCollapsed()">

  <!-- Toggle Button -->
  <button (click)="toggleSidebar()" aria-label="Toggle sidebar"
    class="absolute -right-3 top-6 w-6 h-6 rounded-full bg-[#506ef7] border-2 border-[#1e1e1e] flex items-center justify-center text-white z-50 hover:bg-[#4057db] transition-colors shadow-md">
    <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 transition-transform duration-300"
      [class.rotate-180]="isCollapsed()" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
    </svg>
  </button>

  <!-- Logo section -->
  <div class="h-16 flex items-center px-6 border-b border-[#ffffff10] overflow-hidden">
    <div class="flex items-center gap-3 min-w-0">
      <div class="w-8 h-8 rounded-lg bg-gradient-to-br from-[#506ef7] to-[#8095fb] flex-shrink-0 flex items-center justify-center shadow-lg shadow-[#506ef7]/30">
        <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      </div>
      <div class="flex flex-col overflow-hidden min-w-0 transition-opacity duration-150 group cursor-default mt-1"
        [class.opacity-0]="isCollapsed()">
        <span class="font-black text-xl tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-white via-blue-50 to-[#8095fb] whitespace-nowrap leading-none drop-shadow-sm">Discont</span>
        <span class="text-[8px] font-bold text-[var(--text-muted)] uppercase tracking-[0.12em] whitespace-nowrap opacity-60 group-hover:opacity-100 group-hover:text-[#8095fb] transition-all duration-300 mt-0.5">Distributed Content</span>
      </div>
    </div>
  </div>

  <!-- Navigation -->
  <nav class="min-h-0 flex-1 overflow-y-auto overflow-x-hidden py-6 px-4 flex flex-col gap-1.5" aria-label="Main navigation">

    <div class="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-wider mb-2 ml-2 mt-4 whitespace-nowrap transition-opacity duration-150"
      [class.opacity-0]="isCollapsed()">Menu</div>

    @for (item of visibleMenuItems; track item.name) {
      <a [routerLink]="item.path" routerLinkActive="active-link" [title]="isCollapsed() ? item.name : ''"
        class="group flex items-center gap-3 pl-3.5 pr-3 py-2.5 rounded-[var(--border-radius)] text-[var(--text-muted)] hover:text-white hover:bg-[#ffffff08] transition-colors duration-300 relative overflow-hidden"
        [style.margin-top]="item.name === 'My Team' ? 'auto' : null">
        <div class="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-0 bg-[#506ef7] rounded-r-md transition-all duration-300 group-[.active-link]:h-6 group-hover:h-4 group-[.active-link]:shadow-[0_0_8px_#506ef7]"></div>
        <div class="flex items-center justify-center flex-shrink-0 w-5 h-5 transition-transform duration-300 group-hover:scale-110 group-[.active-link]:text-[#506ef7]">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" class="w-full h-full drop-shadow-sm" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="item.icon" />
            @if (item.name === 'Content Settings') {
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            }
          </svg>
        </div>
        <span class="font-medium text-sm group-[.active-link]:text-white group-[.active-link]:font-semibold transition-opacity duration-150 whitespace-nowrap"
          [class.opacity-0]="isCollapsed()">{{ item.name }}</span>
        <div class="absolute inset-0 bg-gradient-to-r from-transparent via-[#ffffff05] to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]"></div>
      </a>
    }
  </nav>

  <!-- Bottom Profile Section -->
  <button (click)="openProfile()" type="button"
    class="shrink-0 mt-auto flex w-full items-center border-0 border-t border-[#ffffff10] bg-transparent pl-5 pr-4 py-3 text-left hover:bg-[#ffffff08] transition-colors cursor-pointer"
    [class.gap-3]="!isCollapsed()"
    [attr.aria-label]="'Open profile settings for ' + (auth.currentUser()?.first_name || 'user')">
      <div class="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center font-bold text-white shadow-md relative overflow-hidden">
        @if (auth.currentUser()?.profile_photo_url) {
          <img [src]="auth.currentUser().profile_photo_url" alt="Profile photo" class="w-full h-full object-cover" />
        } @else {
          <div class="w-full h-full bg-gradient-to-tr from-purple-500 to-[#506ef7] flex items-center justify-center font-bold text-white">
            {{ auth.currentUser()?.first_name ? auth.currentUser().first_name[0].toUpperCase() : 'U' }}
          </div>
        }
      </div>
      @if (!isCollapsed()) {
        <div class="flex min-w-0 flex-1 flex-col overflow-hidden">
          <span class="text-sm font-semibold truncate text-white whitespace-nowrap">{{ auth.currentUser()?.first_name || 'User' }} {{ auth.currentUser()?.last_name || '' }}</span>
          <span class="text-xs text-[var(--text-muted)] truncate whitespace-nowrap">
            @if (auth.userLoading()) {
              Loading...
            } @else {
              {{ auth.currentUser()?.email ?? '—' }}
            }
          </span>
        </div>
        <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 text-[var(--text-muted)] flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
        </svg>
      }
  </button>

  <!-- Profile Settings Modal Overlay -->
  @if (isProfileOpen()) {
    <div class="profile-modal-backdrop fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] flex items-center justify-center"
      [class.profile-modal-backdrop--closing]="profileClosing()"
      role="dialog" aria-modal="true" aria-labelledby="profile-modal-title"
      (click)="closeProfile()">
      <div class="profile-modal-panel bg-[var(--bg-panel)] border border-[#ffffff10] rounded-2xl w-full max-w-md shadow-2xl overflow-hidden relative max-h-[90vh] overflow-y-auto"
        [class.profile-modal-panel--closing]="profileClosing()"
        (click)="$event.stopPropagation()">

        <!-- Header -->
        <div class="flex items-center justify-between p-6 border-b border-[#ffffff10]">
          <div>
            <h2 id="profile-modal-title" class="text-xl font-bold text-white">Profile Settings</h2>
            <p class="text-sm text-[var(--text-muted)] mt-1 flex items-center gap-2">
              <span>Your role:</span>
              <span class="px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wider bg-[#506ef7]/20 text-[#8095fb]">{{ currentUserRoleLabel() }}</span>
            </p>
          </div>
          <button (click)="closeProfile()" type="button" aria-label="Close profile settings"
            class="text-[var(--text-muted)] hover:text-white transition-colors bg-[#ffffff05] hover:bg-[#ffffff10] p-1.5 rounded-lg border border-[#ffffff10]">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <div class="p-6 flex flex-col gap-6">

          <!-- Avatar + photo upload -->
          <div class="flex flex-col items-center gap-3">
            <div class="relative w-20 h-20 group">
              <div class="w-20 h-20 rounded-full overflow-hidden border-2 border-[#ffffff10] shadow-lg">
                @if (auth.currentUser()?.profile_photo_url) {
                  <img [src]="auth.currentUser().profile_photo_url" alt="Profile photo" class="w-full h-full object-cover" />
                } @else {
                  <div class="w-full h-full bg-gradient-to-tr from-purple-500 to-[#506ef7] flex items-center justify-center font-bold text-white text-3xl">
                    {{ auth.currentUser()?.first_name ? auth.currentUser().first_name[0].toUpperCase() : 'U' }}
                  </div>
                }
              </div>
              @if (photoUploading()) {
                <div class="absolute inset-0 rounded-full bg-black/60 flex items-center justify-center">
                  <svg class="w-6 h-6 text-white animate-spin" fill="none" viewBox="0 0 24 24" aria-label="Uploading photo">
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                  </svg>
                </div>
              } @else {
                <button type="button" (click)="triggerPhotoInput(photoInput)"
                  class="absolute inset-0 rounded-full bg-black/60 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  aria-label="Change profile photo">
                  <svg class="w-6 h-6 text-white mb-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z"/>
                  </svg>
                </button>
              }
            </div>
            <input #photoInput type="file" accept="image/jpeg,image/png,image/webp" class="sr-only"
              aria-label="Upload profile photo" (change)="onPhotoChange($event)" />
            <button type="button" (click)="triggerPhotoInput(photoInput)"
              class="text-xs font-medium text-[#506ef7] hover:text-[#8095fb] transition-colors"
              [disabled]="photoUploading()">
              {{ photoUploading() ? 'Uploading...' : 'Change Photo' }}
            </button>
          </div>

          <!-- Profile form -->
          <form [formGroup]="profileForm" (ngSubmit)="saveProfile()" id="profile-form" novalidate>
            <div class="flex flex-col gap-4">
              <div class="flex gap-4">
                <div class="flex-1">
                  <label for="first-name" class="block text-[10px] font-bold text-[var(--text-muted)] mb-1 uppercase tracking-wider ml-1">First Name</label>
                  <input id="first-name" type="text" formControlName="first_name"
                    class="w-full px-4 py-2 bg-[#ffffff05] border border-[#ffffff10] rounded-lg text-white focus:outline-none focus:border-[#506ef7] focus:ring-1 focus:ring-[#506ef7] transition-all"
                    autocomplete="given-name" />
                </div>
                <div class="flex-1">
                  <label for="last-name" class="block text-[10px] font-bold text-[var(--text-muted)] mb-1 uppercase tracking-wider ml-1">Last Name</label>
                  <input id="last-name" type="text" formControlName="last_name"
                    class="w-full px-4 py-2 bg-[#ffffff05] border border-[#ffffff10] rounded-lg text-white focus:outline-none focus:border-[#506ef7] focus:ring-1 focus:ring-[#506ef7] transition-all"
                    autocomplete="family-name" />
                </div>
              </div>
              <div>
                <label for="email" class="block text-[10px] font-bold text-[var(--text-muted)] mb-1 uppercase tracking-wider ml-1">Email Address</label>
                <input id="email" type="email" [value]="auth.currentUser()?.email || ''" disabled
                  class="w-full px-4 py-2 bg-[#ffffff05] border border-[#ffffff10] rounded-lg text-[var(--text-muted)] cursor-not-allowed"
                  autocomplete="email" aria-describedby="email-hint" />
                <p id="email-hint" class="text-[10px] text-[var(--text-muted)] mt-1 ml-1">Email cannot be changed</p>
              </div>
            </div>

            @if (saveError()) {
              <div class="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg" role="alert">
                <p class="text-xs text-red-400">{{ saveError() }}</p>
              </div>
            }
            @if (saveSuccess()) {
              <div class="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg" role="status">
                <p class="text-xs text-green-400">Profile updated successfully!</p>
              </div>
            }
          </form>

          <!-- Change Password -->
          <div class="border border-[#ffffff10] rounded-lg overflow-hidden">
            <button type="button" (click)="showPasswordChange.set(!showPasswordChange())"
              class="w-full flex items-center justify-between p-3 bg-[#ffffff05] hover:bg-[#ffffff08] transition-colors"
              [attr.aria-expanded]="showPasswordChange()">
              <div class="flex flex-col text-left">
                <span class="text-sm font-medium text-white">Change Password</span>
                <span class="text-xs text-[var(--text-muted)]">Secure your account</span>
              </div>
              <svg class="w-4 h-4 text-[var(--text-muted)] transition-transform" [class.rotate-180]="showPasswordChange()"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            @if (showPasswordChange()) {
              <form [formGroup]="passwordForm" (ngSubmit)="changePassword()" class="p-4 flex flex-col gap-3 border-t border-[#ffffff10]" novalidate>
                <div>
                  <label for="current-password" class="block text-[10px] font-bold text-[var(--text-muted)] mb-1 uppercase tracking-wider ml-1">Current Password</label>
                  <input id="current-password" type="password" formControlName="current_password"
                    class="w-full px-4 py-2 bg-[#ffffff05] border border-[#ffffff10] rounded-lg text-white focus:outline-none focus:border-[#506ef7] focus:ring-1 focus:ring-[#506ef7] transition-all"
                    autocomplete="current-password" />
                </div>
                <div>
                  <label for="new-password" class="block text-[10px] font-bold text-[var(--text-muted)] mb-1 uppercase tracking-wider ml-1">New Password</label>
                  <input id="new-password" type="password" formControlName="new_password"
                    class="w-full px-4 py-2 bg-[#ffffff05] border border-[#ffffff10] rounded-lg text-white focus:outline-none focus:border-[#506ef7] focus:ring-1 focus:ring-[#506ef7] transition-all"
                    autocomplete="new-password" aria-describedby="new-password-hint" />
                  <p id="new-password-hint" class="text-[10px] text-[var(--text-muted)] mt-1 ml-1">Minimum 8 characters</p>
                </div>
                <div>
                  <label for="confirm-password" class="block text-[10px] font-bold text-[var(--text-muted)] mb-1 uppercase tracking-wider ml-1">Confirm New Password</label>
                  <input id="confirm-password" type="password" formControlName="confirm_password"
                    class="w-full px-4 py-2 bg-[#ffffff05] border border-[#ffffff10] rounded-lg text-white focus:outline-none focus:border-[#506ef7] focus:ring-1 focus:ring-[#506ef7] transition-all"
                    autocomplete="new-password" />
                </div>

                @if (passwordError()) {
                  <div class="p-3 bg-red-500/10 border border-red-500/30 rounded-lg" role="alert">
                    <p class="text-xs text-red-400">{{ passwordError() }}</p>
                  </div>
                }
                @if (passwordSuccess()) {
                  <div class="p-3 bg-green-500/10 border border-green-500/30 rounded-lg" role="status">
                    <p class="text-xs text-green-400">Password changed successfully!</p>
                  </div>
                }

                <button type="submit" [disabled]="passwordForm.invalid || saving()"
                  class="w-full px-4 py-2 rounded-lg bg-[#506ef7] hover:bg-[#4057db] disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium text-sm transition-all flex items-center justify-center gap-2">
                  @if (saving()) {
                    <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                      <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                      <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                  }
                  Update Password
                </button>
              </form>
            }
          </div>

        </div>

        <!-- Footer actions -->
        <div class="flex justify-between gap-3 p-6 pt-0">
          <button type="button" (click)="logout()"
            class="px-4 py-2 rounded-lg text-red-400 hover:text-red-300 hover:bg-red-400/10 transition-colors font-medium text-sm flex items-center gap-2">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Logout
          </button>
          <div class="flex gap-3">
            <button type="button" (click)="closeProfile()"
              class="px-4 py-2 rounded-lg text-white hover:bg-[#ffffff10] transition-colors font-medium text-sm">Cancel</button>
            <button type="submit" form="profile-form" [disabled]="profileForm.invalid || saving() || !hasProfileChanges()"
              class="px-4 py-2 rounded-lg bg-[#506ef7] hover:bg-[#4057db] disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer text-white font-medium shadow-[0_4px_14px_0_rgba(80,110,247,0.39)] transition-all text-sm flex items-center gap-2">
              @if (saving()) {
                <svg class="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                </svg>
              }
              Save Settings
            </button>
          </div>
        </div>

      </div>
    </div>
  }
</aside>
